import { useState, useEffect } from 'react';
import { Sunrise, TrendingUp, TrendingDown, AlertTriangle, Loader2, Calendar, Clock, Database, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyBriefingCard() {
  const [briefing, setBriefing] = useState(() => {
    // Load from cache for instant display - 30 min TTL
    try {
      const cached = localStorage.getItem('dailyBriefing_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 30 * 60 * 1000) { // 30 min TTL
          return data;
        }
      }
    } catch (e) {}
    return null;
  });
  const [loading, setLoading] = useState(!briefing); // No loading if cache exists
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadBriefing();
  }, []);

  const loadBriefing = async () => {
    // Never show loading if we have cached data
    if (!briefing) setLoading(true);
    try {
      const response = await axios.get(`${API}/ai/daily-briefing`);
      setBriefing(response.data);
      
      // Cache for instant display (30 min TTL)
      try {
        localStorage.setItem('dailyBriefing_cache', JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {}
    } catch (error) {
      console.error('Error loading briefing:', error);
    }
    setLoading(false);
  };

  const formatGeneratedTime = (isoString) => {
    if (!isoString) return 'Just now';
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return 'Just now';
    }
  };

  // Get first 2 lines for preview
  const getPreviewText = (text) => {
    if (!text) return '';
    const lines = text.split('\n').filter(line => line.trim());
    return lines.slice(0, 2).join('\n');
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 shadow-lg">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
          <span className="text-sm font-medium text-zinc-200">AI is analyzing market conditions...</span>
        </div>
      </div>
    );
  }

  if (!briefing) {
    return null;
  }

  // Market Closed State (Weekend/Holiday)
  if (briefing.is_market_closed) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 shadow-lg" data-testid="daily-briefing">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-zinc-400" />
            <h2 className="text-base font-semibold text-white">Market Closed</h2>
            <Badge className="bg-zinc-700 text-zinc-200 hover:bg-zinc-700 text-xs">
              {briefing.closed_reason}
            </Badge>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-zinc-400 hover:text-white"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
        
        {isExpanded && (
          <div className="mt-3 bg-zinc-800 rounded-lg p-3">
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed text-zinc-300">
              {briefing.briefing}
            </pre>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-cyan-800/50 rounded-lg p-4 shadow-lg" data-testid="daily-briefing">
      {/* Header - Always visible */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sunrise className="w-5 h-5 text-amber-400" />
          <h2 className="text-base font-semibold text-white">AI Daily Briefing</h2>
          <Badge className={`text-xs ${
            briefing.market_status === 'STRONG_BULLISH' ? 'bg-emerald-600 text-white' :
            briefing.market_status === 'BULLISH' ? 'bg-green-600 text-white' :
            briefing.market_status === 'BEARISH' ? 'bg-red-600 text-white' :
            briefing.market_status === 'STRONG_BEARISH' ? 'bg-red-700 text-white' :
            'bg-zinc-600 text-white'
          }`}>
            {briefing.market_status}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">{formatGeneratedTime(briefing.generated_at)}</span>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-zinc-400 hover:text-white h-7 px-2"
          >
            {isExpanded ? (
              <>
                <span className="text-xs mr-1">Less</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="text-xs mr-1">More</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Preview - Always visible (first 2 lines) */}
      {!isExpanded && (
        <div className="mt-2 text-sm text-zinc-300 line-clamp-2">
          {getPreviewText(briefing.briefing)}
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <>
          {/* Data Source Banner */}
          {briefing.data_source && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg mt-3 text-xs ${
              briefing.is_market_open 
                ? 'bg-emerald-900/30 border border-emerald-700/50 text-emerald-300' 
                : 'bg-amber-900/30 border border-amber-700/50 text-amber-300'
            }`}>
              <Database className="w-3 h-3" />
              <span>{briefing.data_source}</span>
              {!briefing.is_market_open && (
                <span className="text-amber-400 ml-2">• Market Closed</span>
              )}
            </div>
          )}

          {/* AI Briefing Content */}
          <div className="mt-3 bg-zinc-800 rounded-lg p-3">
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed text-zinc-200">
              {briefing.briefing}
            </pre>
          </div>

          {/* Footer */}
          <div className="mt-2 text-xs text-zinc-500">
            Powered by GPT-4o
          </div>
        </>
      )}
    </div>
  );
}
